import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import { ErrorMessage } from '@montugroup/circuit-shared/components/common/ErrorMessage';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@montugroup/design-system';
import { Logger } from '@montugroup/utilities';
import InfoIcon from '@mui/icons-material/Info';
import { Grid2 } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

const logger = new Logger('UseOrDeleteExistingOrderModal');

const client = getTsApiV1Client();
const customerClient = getTsCustomerPortalClient();

export function UseOrDeleteExistingOrderModal() {
  const [customDialogError, setCustomDialogError] = useState<string>();
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const { mutate, isPending, error, contractEndpoint, reset } = customerClient.cancelUnpaidAdminOrder.useMutation({
    onSuccess: () => {
      logger.info('cancelOrder: successfully cancelled existing admin order for customer');
      queryClient.invalidateQueries({ queryKey: client.order.getPatientRefill.keys });
    },
  });
  const { data: refillData, isLoading: refillIsLoading } = client.order.getPatientRefill.useQuery({
    queryKey: client.order.getPatientRefill.keys,
    queryData: {
      query: {},
      params: {
        userId: Number(user!.id),
      },
    },
    enabled: !!user?.id && !Number.isNaN(Number(user.id)),
  });

  const existingAdminOrder = refillData?.body.adminOrder;
  const hasExistingAdminOrder = Boolean(!refillIsLoading && existingAdminOrder);
  const orderProducts = refillData?.body.order?.OrderProducts;

  useEffect(() => {
    if (hasExistingAdminOrder && (!orderProducts || orderProducts.length === 0)) {
      logger.critical('rendering dialog without any orderProducts', {
        existingAdminOrder,
        orderProducts,
      });
    }
  }, [existingAdminOrder, hasExistingAdminOrder, orderProducts]);

  const clearErrors = () => {
    setCustomDialogError(undefined);
    reset();
  };

  const handleStartNewOrder = () => {
    clearErrors();

    if (existingAdminOrder?.id) {
      // Cancelling the existing order allows the user to start a new order, otherwise this modal is blocking
      mutate({ body: { data: { orderId: existingAdminOrder.id } } });
    } else {
      logger.error('handleStartNewOrder: existingAdminOrder.id', {
        existingAdminOrder,
        orderProducts,
      });
      setCustomDialogError(
        'There was a problem cancelling the order please try again, or contact support if the problem persists.',
      );
    }
  };

  const navigateToCheckout = () => {
    clearErrors();

    if (existingAdminOrder?.paymentUrl) {
      logger.info('navigateToCheckout: navigating to existing checkout page');
      window.location.replace(existingAdminOrder.paymentUrl);
    } else {
      logger.error('navigateToCheckout: undefined existingAdminOrder.paymentUrl', {
        existingAdminOrder,
        orderProducts,
      });
      setCustomDialogError(
        'There was a problem navigating to the checkout page please try again, or contact support if the problem persists.',
      );
    }
  };

  return (
    <Dialog open={hasExistingAdminOrder} fullWidth>
      <DialogTitle>
        <InfoIcon color="info" />
        You have a pending order
      </DialogTitle>
      <DialogContent>
        <Stack gap={3} direction={'column'}>
          <Typography variant="body1">uMeds support has created an order for:</Typography>

          <Box sx={{ overflowY: 'auto', maxHeight: 200 }}>
            <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
            {orderProducts?.map((product) => {
              return (
                <Box key={product.id}>
                  <Box
                    sx={{ marginTop: 2, marginBottom: 2 }}
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'space-between'}
                  >
                    <Grid2 container flexGrow={1}>
                      <Grid2 size={{ xs: 2 }}>
                        <Typography variant="body1">
                          {product.quantity} unit{product.quantity !== 1 ? 's' : ''}
                        </Typography>
                      </Grid2>
                      <Grid2 size={{ xs: 10 }}>
                        <Typography variant="body1" fontWeight={'bold'}>
                          {product.Product.name}
                        </Typography>
                      </Grid2>
                    </Grid2>
                  </Box>
                  <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
                </Box>
              );
            })}
          </Box>

          <Typography variant="body1">
            You can continue to pay for the order now or cancel and start a new order if your needs have changed
          </Typography>
        </Stack>
      </DialogContent>
      {customDialogError && (
        <Typography
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.primary.main,
            marginRight: theme.spacing(6),
            marginLeft: theme.spacing(6),
          })}
        >
          {customDialogError}
        </Typography>
      )}
      <ErrorMessage error={error} contractEndpoint={contractEndpoint} />
      <DialogActions>
        <Button variant="outlined" loading={isPending} onClick={() => handleStartNewOrder()}>
          Cancel and start new a order
        </Button>
        <Button variant="contained" disabled={isPending} onClick={() => navigateToCheckout()}>
          Proceed to checkout
        </Button>
      </DialogActions>
    </Dialog>
  );
}
